import React, { useState } from 'react';
import { CompleteKundali, Language, ChartStyle, PlanetPosition } from '../../types/kundali';
import { 
  ArrowLeftRight, 
  ChevronDown, 
  ChevronUp, 
  Layers, 
  Maximize2, 
  Minimize2, 
  Compass, 
  Sparkles 
} from 'lucide-react';
import { NorthIndianSvgChart, SouthIndianSvgChart, EastIndianSvgChart } from '../ChartViewer';

interface DualChartContextPanelProps {
  kundali: CompleteKundali;
  lang?: Language;
  highlightedPlanet?: string | null;
  onSelectPlanet?: (planetName: string) => void;
}

const AVAILABLE_VARGAS: { code: string; labelEn: string; labelHi: string }[] = [
  { code: 'D1', labelEn: 'D1 — Rashi (Natal Lagna)', labelHi: 'D1 — राशि कुण्डली' },
  { code: 'D9', labelEn: 'D9 — Navamsha (Dharma & Destiny)', labelHi: 'D9 — नवांश कुण्डली' },
  { code: 'Chandra', labelEn: 'Chandra — Moon Chart', labelHi: 'चन्द्र कुण्डली' },
  { code: 'Surya', labelEn: 'Surya — Sun Chart', labelHi: 'सूर्य कुण्डली' },
  { code: 'D2', labelEn: 'D2 — Hora (Wealth & Prosperity)', labelHi: 'D2 — होरा' },
  { code: 'D3', labelEn: 'D3 — Drekkana (Siblings & Courage)', labelHi: 'D3 — द्रेष्काण' },
  { code: 'D4', labelEn: 'D4 — Chaturthamsha (Fortune & Assets)', labelHi: 'D4 — चतुर्थांश' },
  { code: 'D7', labelEn: 'D7 — Saptamsha (Children & Progeny)', labelHi: 'D7 — सप्तांश' },
  { code: 'D10', labelEn: 'D10 — Dashamsha (Career & Status)', labelHi: 'D10 — दशमांश' },
  { code: 'D12', labelEn: 'D12 — Dvadamsha (Parents & Lineage)', labelHi: 'D12 — द्वादशांश' },
  { code: 'D16', labelEn: 'D16 — Shodashamsha (Vehicles & Comforts)', labelHi: 'D16 — षोडशांश' },
  { code: 'D20', labelEn: 'D20 — Vimshamsha (Spiritual Progress)', labelHi: 'D20 — विंशांश' },
  { code: 'D24', labelEn: 'D24 — Chaturvimshamsha (Learning & Intellect)', labelHi: 'D24 — चतुर्विंशांश' },
  { code: 'D27', labelEn: 'D27 — Saptavimshamsha (Strengths & Weaknesses)', labelHi: 'D27 — सप्तविंशांश' },
  { code: 'D30', labelEn: 'D30 — Trimshamsha (Misfortunes & Arishta)', labelHi: 'D30 — त्रिंशांश' },
  { code: 'D40', labelEn: 'D40 — Khavedamsha (Auspicious Effects)', labelHi: 'D40 — खवेदांश' },
  { code: 'D45', labelEn: 'D45 — Akshavedamsha (General Well-being)', labelHi: 'D45 — अक्षवेदांश' },
  { code: 'D60', labelEn: 'D60 — Shashtiamsha (All Past Karmas)', labelHi: 'D60 — षष्ट्यंश' }
];

export const DualChartContextPanel: React.FC<DualChartContextPanelProps> = ({
  kundali,
  lang = 'en',
  highlightedPlanet,
  onSelectPlanet
}) => {
  const isHi = lang === 'hi';
  const [chart1Code, setChart1Code] = useState<string>('D1');
  const [chart2Code, setChart2Code] = useState<string>('D9');
  const [chartStyle, setChartStyle] = useState<ChartStyle>('north');
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const swapCharts = () => {
    setChart1Code(chart2Code);
    setChart2Code(chart1Code);
  };

  const getChartData = (code: string) => {
    if (code === 'Chandra') return kundali.charts?.Chandra || kundali.charts?.D1;
    if (code === 'Surya') return kundali.charts?.Surya || kundali.charts?.D1;
    return kundali.charts?.[code] || kundali.charts?.D1;
  };

  const chart1Data = getChartData(chart1Code);
  const chart2Data = getChartData(chart2Code);

  const chart1Planets = chart1Data?.planets || kundali.planets;
  const chart2Planets = chart2Data?.planets || kundali.planets;

  const ChartRenderer = chartStyle === 'north' ? NorthIndianSvgChart : chartStyle === 'south' ? SouthIndianSvgChart : EastIndianSvgChart;

  return (
    <div className="bg-[#FFFDF7] border border-[#B58A4A]/30 rounded-2xl shadow-sm overflow-hidden mb-5">
      {/* Header bar */}
      <div className="bg-[#174C4B]/5 px-4 py-3 border-b border-[#B58A4A]/20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-[#B58A4A]" />
          <h2 className="font-serif font-bold text-sm text-[#174C4B] tracking-wide">
            {isHi ? 'दोहरी कुण्डली संदर्भ (Dual Chart Context)' : 'Dual Chart Context'}
          </h2>
          {highlightedPlanet && (
            <span className="text-2xs font-semibold px-2 py-0.5 rounded-full bg-[#B58A4A]/20 text-[#8E692B] border border-[#B58A4A]/30">
              Focus: {highlightedPlanet}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Style Selector */}
          <div className="flex items-center bg-white border border-stone-200 rounded-lg p-0.5 text-2xs font-medium">
            <button
              onClick={() => setChartStyle('north')}
              className={`px-2 py-0.5 rounded ${chartStyle === 'north' ? 'bg-[#174C4B] text-white font-semibold' : 'text-stone-600 hover:text-stone-900'}`}
            >
              {isHi ? 'उत्तर' : 'North'}
            </button>
            <button
              onClick={() => setChartStyle('south')}
              className={`px-2 py-0.5 rounded ${chartStyle === 'south' ? 'bg-[#174C4B] text-white font-semibold' : 'text-stone-600 hover:text-stone-900'}`}
            >
              {isHi ? 'दक्षिण' : 'South'}
            </button>
            <button
              onClick={() => setChartStyle('east')}
              className={`px-2 py-0.5 rounded ${chartStyle === 'east' ? 'bg-[#174C4B] text-white font-semibold' : 'text-stone-600 hover:text-stone-900'}`}
            >
              {isHi ? 'पूर्व' : 'East'}
            </button>
          </div>

          {/* Swap button */}
          <button
            onClick={swapCharts}
            className="p-1 rounded-md bg-white hover:bg-stone-100 text-stone-600 border border-stone-200 text-2xs flex items-center gap-1 font-medium transition-colors"
            title="Swap Chart 1 and Chart 2"
          >
            <ArrowLeftRight className="w-3.5 h-3.5 text-[#B58A4A]" />
          </button>

          {/* Collapse/Expand Toggle */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 rounded-md bg-white hover:bg-stone-100 text-stone-600 border border-stone-200 transition-colors"
            title={isCollapsed ? 'Expand Charts' : 'Collapse Charts'}
          >
            {isCollapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Chart 1 Column */}
            <div className="flex flex-col bg-white border border-[#B58A4A]/20 rounded-xl p-3 shadow-2xs">
              <div className="flex items-center justify-between mb-2">
                <label className="text-2xs font-semibold text-[#174C4B] uppercase tracking-wider">
                  {isHi ? 'प्रथम कुण्डली (Chart 1)' : 'Chart 1'}
                </label>
                <select
                  value={chart1Code}
                  onChange={(e) => setChart1Code(e.target.value)}
                  className="text-xs bg-[#FFFDF7] border border-[#B58A4A]/40 rounded-md px-2 py-1 font-medium text-[#174C4B] focus:outline-none"
                >
                  {AVAILABLE_VARGAS.map(v => (
                    <option key={v.code} value={v.code}>
                      {isHi ? v.labelHi : v.labelEn}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative aspect-square w-full max-w-[340px] mx-auto flex items-center justify-center">
                {chart1Data ? (
                  <ChartRenderer
                    chartData={chart1Data}
                    planets={chart1Planets}
                    onSelectPlanet={(p: PlanetPosition) => onSelectPlanet && onSelectPlanet(p.name)}
                    lang={lang}
                  />
                ) : (
                  <div className="text-xs text-stone-400 italic">Chart data unavailable</div>
                )}
              </div>
              <div className="text-center mt-2 text-2xs text-stone-500 font-medium">
                {AVAILABLE_VARGAS.find(v => v.code === chart1Code)?.labelEn}
              </div>
            </div>

            {/* Chart 2 Column */}
            <div className="flex flex-col bg-white border border-[#B58A4A]/20 rounded-xl p-3 shadow-2xs">
              <div className="flex items-center justify-between mb-2">
                <label className="text-2xs font-semibold text-[#174C4B] uppercase tracking-wider">
                  {isHi ? 'द्वितीय कुण्डली (Chart 2)' : 'Chart 2'}
                </label>
                <select
                  value={chart2Code}
                  onChange={(e) => setChart2Code(e.target.value)}
                  className="text-xs bg-[#FFFDF7] border border-[#B58A4A]/40 rounded-md px-2 py-1 font-medium text-[#174C4B] focus:outline-none"
                >
                  {AVAILABLE_VARGAS.map(v => (
                    <option key={v.code} value={v.code}>
                      {isHi ? v.labelHi : v.labelEn}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative aspect-square w-full max-w-[340px] mx-auto flex items-center justify-center">
                {chart2Data ? (
                  <ChartRenderer
                    chartData={chart2Data}
                    planets={chart2Planets}
                    onSelectPlanet={(p: PlanetPosition) => onSelectPlanet && onSelectPlanet(p.name)}
                    lang={lang}
                  />
                ) : (
                  <div className="text-xs text-stone-400 italic">Chart data unavailable</div>
                )}
              </div>
              <div className="text-center mt-2 text-2xs text-stone-500 font-medium">
                {AVAILABLE_VARGAS.find(v => v.code === chart2Code)?.labelEn}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
